import { cn } from "@tellescope/design-system";

import { formatName } from "@tellescope/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CONTACT_TIMELINE_TAKEOVER_BLOCK_ID } from "../../ContactDetailsHeader/constants";
import { BlockProps } from "../../../types";
import { useTimelineBlock } from "../../../hooks";
import { BlockHeader } from "../BlockHeader";
import { BlockContent } from "../BlockContent";
import { TimelineFilterPopover } from "./TimelineFilterPopover";
import { TimelineItem } from "./cards";
import { BlockPaginatedList } from "../BlockPaginatedList";
import { BlockEmptyState } from "../BlockEmptyState";

export const TimelineBlock = ({
  onMaximize,
  alwaysShowButtons = false,
  className,
  contentClassName,
  isFeatureEnabled,
  blockId,
  ...rest
}: BlockProps) => {
  const { i18n } = useTranslation();
  const { t } = useTranslation("timeline");
  const {
    parsedInfo,
    enduserId,
    filterBlock,
    filterFieldOptions,
    onFilterChange,
    combineSMS,
    setCombineSMS,
    items,
    isLoading,
    fetchNextPage,
    enduserQuery,
    isEditingTitle,
    setIsEditingTitle,
    newTitle,
    setNewTitle,
    editMode,
    setEditMode,
    updateInfo,
    duplicateBlock,
    deleteBlock,
    toggleExpanded,
    expanded,
    blockLayout,
    canEditTitle,
    canEditBlock,
    canCreateBlock,
    canDeleteBlock,
  } = useTimelineBlock({ ...rest, blockId, isFeatureEnabled, locale: i18n.language });

  const enduser = enduserQuery.data;
  const enduserName = enduser ? formatName(enduser) : "";
  // The takeover-page timeline has nothing to persist filter/combine-SMS/duplicate/delete changes to —
  // its view is never fetched, so useBlock's permission check fail-opens canCreateBlock/canDeleteBlock
  // to true, and any write silently no-ops (useBlockMutations resolves against an empty, uncached
  // view). Suppress every one of those controls entirely rather than ship them live-looking but dead.
  const isTakeoverTimeline = blockId === CONTACT_TIMELINE_TAKEOVER_BLOCK_ID;

  const [openItemKey, setOpenItemKey] = useState<string>();

  return (
    <div className={cn("group", className)}>
      <BlockHeader
        editMode={editMode}
        setEditMode={setEditMode}
        isEditingTitle={isEditingTitle}
        setIsEditingTitle={setIsEditingTitle}
        newTitle={newTitle}
        setNewTitle={setNewTitle}
        canEditTitle={canEditTitle}
        parsedInfo={parsedInfo}
        updateInfo={updateInfo}
        duplicateBlock={duplicateBlock}
        deleteBlock={deleteBlock}
        canCreateBlock={canCreateBlock && !isTakeoverTimeline}
        canDeleteBlock={canDeleteBlock && !isTakeoverTimeline}
        onMaximize={onMaximize}
        alwaysShowButtons={alwaysShowButtons}
        actions={
          canEditBlock &&
          !isTakeoverTimeline && (
            <TimelineFilterPopover
              filterBlock={filterBlock}
              fieldOptions={filterFieldOptions}
              onFilterChange={onFilterChange}
              combineSMS={combineSMS}
              onCombineSMSChange={setCombineSMS}
            />
          )
        }
      />

      <BlockContent
        editMode={editMode}
        setEditMode={setEditMode}
        expanded={expanded}
        layout={blockLayout}
        onToggleExpanded={() => toggleExpanded()}
        className={cn("flex flex-col", contentClassName)}
      >
        {isLoading ? (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("Loading timeline...")}</BlockEmptyState>
          </div>
        ) : !items.length ? (
          <div className="flex flex-1 items-center justify-center">
            <BlockEmptyState>{t("No items found")}</BlockEmptyState>
          </div>
        ) : (
          <BlockPaginatedList
            className="flex flex-col flex-grow"
            scrollbar="floating"
            fadeWhenClipped
            onBottomReached={() => fetchNextPage()}
          >
            {items.map((item) => {
              const key = item._type === "form_response_group" ? `form-${item.formId}` : `${item._type}-${item.id}`;
              return (
                <TimelineItem
                  key={key}
                  currentEnduserId={enduserId}
                  enduserName={enduserName}
                  item={item}
                  className="mb-2"
                  open={openItemKey === key}
                  onOpenChange={(next) => setOpenItemKey(next ? key : undefined)}
                />
              );
            })}
          </BlockPaginatedList>
        )}
      </BlockContent>
    </div>
  );
};
